import { useState, useEffect } from 'react';
import { Station, PopulationSource, ProjectConfig } from '../types';
import { updateStationPopulation } from '../utils/population';

interface UseStationFormProps {
  station: Station;
  onUpdate: (updates: Partial<Station>) => void;
  onClose?: () => void;
  config: ProjectConfig;
}

interface ParsedCoordinates {
  latitude: number | null;
  longitude: number | null;
}

const formatValue = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

export const useStationForm = ({ station, onUpdate, onClose, config }: UseStationFormProps) => {
  const [name, setName] = useState(station.name);
  const [population, setPopulation] = useState(formatValue(station.population));
  const [latitude, setLatitude] = useState<number | null>(station.latitude || null);
  const [longitude, setLongitude] = useState<number | null>(station.longitude || null);
  const [isLoading, setIsLoading] = useState(false);
  const [populationSource, setPopulationSource] = useState<PopulationSource>(station.population_source || 'manual');
  const [coordinatesSaved, setCoordinatesSaved] = useState(false);
  const [latitudeError, setLatitudeError] = useState(false);
  const [longitudeError, setLongitudeError] = useState(false);

  // Update local state when station changes
  useEffect(() => {
    setName(station.name);
    setPopulation(formatValue(station.population));
    setLatitude(station.latitude || null);
    setLongitude(station.longitude || null);
    setPopulationSource(station.population_source || 'manual');
  }, [station]);

  const parseCoordinateInput = (input: string, fieldType: 'latitude' | 'longitude'): ParsedCoordinates => {
    // Remove any whitespace
    input = input.trim();
    
    // Split by comma if present
    const parts = input.split(',').map(p => p.trim());
    
    // If single value, determine if it's lat or long based on fieldType
    if (parts.length === 1) {
      const value = parseFloat(parts[0]);
      if (isNaN(value)) return { latitude: null, longitude: null };
      
      // Validate based on field type
      if (fieldType === 'latitude' && value >= -90 && value <= 90) {
        return { latitude: value, longitude: null };
      }
      if (fieldType === 'longitude' && value >= -180 && value <= 180) {
        return { latitude: null, longitude: value };
      }
      return { latitude: null, longitude: null };
    }
    
    // If multiple values, parse first two as lat/long
    if (parts.length >= 2) {
      const lat = parseFloat(parts[0]);
      const long = parseFloat(parts[1]);
      
      // Validate both values
      if (!isNaN(lat) && !isNaN(long) && 
          lat >= -90 && lat <= 90 && 
          long >= -180 && long <= 180) {
        return { latitude: lat, longitude: long };
      }
    }
    
    return { latitude: null, longitude: null };
  };

  const handlePopulationChange = (value: string) => {
    // Remove commas and convert to number
    const numValue = parseInt(value.replace(/,/g, ''));
    
    // Only update the display value
    setPopulation(formatValue(numValue));
  };

  const savePopulation = () => {
    const numValue = parseInt(population.replace(/,/g, ''));
    if (!isNaN(numValue) && numValue >= 0 && numValue !== station.population) {
      onUpdate({
        population: numValue,
        population_source: 'manual'
      });
    }
  };

  const handleCoordinateInput = (input: string, fieldType: 'latitude' | 'longitude') => {
    const parsed = parseCoordinateInput(input, fieldType);
    
    // Update state based on what was parsed
    if (parsed.latitude !== null) {
      setLatitude(parsed.latitude);
      setLatitudeError(false);
    }
    if (parsed.longitude !== null) {
      setLongitude(parsed.longitude);
      setLongitudeError(false);
    }
    
    // If invalid input, show error state but don't clear other field
    if (fieldType === 'latitude' && parsed.latitude === null) {
      setLatitudeError(true);
    }
    if (fieldType === 'longitude' && parsed.longitude === null) {
      setLongitudeError(true);
    }
  };

  const handleSaveAndClose = async (shouldClose: boolean = false) => {
    console.log('handleSaveAndClose called:', {
      stationId: station.id,
      shouldClose,
      currentLatitude: latitude,
      currentLongitude: longitude,
      stationLatitude: station.latitude,
      stationLongitude: station.longitude
    });

    setIsLoading(true);
    try {
      // Save any pending changes
      const updates: Partial<Station> = {};
      let needsDelay = false;
      
      // Check if name has changed
      if (name !== station.name) {
        updates.name = name;
      }
      
      // Check if population has changed
      const numPopulation = parseInt(population.replace(/,/g, ''));
      if (!isNaN(numPopulation) && numPopulation >= 0 && numPopulation !== station.population) {
        updates.population = numPopulation;
      }
      
      // Check if coordinates have changed
      if (latitude !== station.latitude || longitude !== station.longitude) {
        console.log('Coordinates changed, preparing update:', {
          stationId: station.id,
          oldLatitude: station.latitude,
          oldLongitude: station.longitude,
          newLatitude: latitude,
          newLongitude: longitude
        });
        updates.latitude = latitude;
        updates.longitude = longitude;
        
        // Always update automated population when coordinates change
        if (latitude !== null && longitude !== null) {
          needsDelay = true;
          console.log('Calling updateStationPopulation API:', {
            stationId: station.id,
            latitude,
            longitude
          });
          const updatedStation = await updateStationPopulation(
            { ...station, latitude, longitude },
            config
          );
          console.log('updateStationPopulation API result:', {
            stationId: station.id,
            oldPopulation: station.circle_population,
            newPopulation: updatedStation.circle_population,
            oldRadius: station.circle_population_radius,
            newRadius: updatedStation.circle_population_radius
          });
          updates.circle_population = updatedStation.circle_population;
          updates.circle_population_radius = updatedStation.circle_population_radius;
        }
      }
      
      // Check if population source has changed
      if (populationSource !== station.population_source) {
        updates.population_source = populationSource;
      }

      // If we have any updates, save them
      if (Object.keys(updates).length > 0) {
        console.log('Saving updates:', {
          stationId: station.id,
          updates
        });
        await onUpdate(updates);
      }

      // Only add delay if we did automatic population calculation
      if (needsDelay) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Only close after all updates are complete
      if (shouldClose && onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Failed to save station changes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePopulationSourceChange = (source: PopulationSource) => {
    setPopulationSource(source);
    onUpdate({ population_source: source });
  };

  return {
    name,
    setName,
    population,
    setPopulation,
    latitude,
    longitude,
    isLoading,
    populationSource,
    coordinatesSaved,
    latitudeError,
    longitudeError,
    handlePopulationChange,
    handleCoordinateInput,
    handleSaveAndClose,
    handlePopulationSourceChange,
    savePopulation
  };
}; 